'use client';

import { useEffect, useRef, useState } from 'react';
import { routePath } from '@/lib/routes';
import { gsap, EASE_OUT, NOT_REDUCED, useGSAP, playOnEnter } from '@/components/sections/kit/motion';
import type { CompareDossierData, Mark, MarkTone } from './types';
import { CONTAINER, Chip, MonoCaption } from './bits';
import DossierHead from './DossierHead';
import { JUMP_EVENT, useChoreo } from './motion';

const ROW_GRID =
  'grid grid-cols-[minmax(300px,1.4fr)_minmax(180px,.9fr)_minmax(180px,.9fr)] gap-px';

const TONE: Record<MarkTone, string> = {
  green: 'text-data-green',
  dim: 'text-dim',
  ink: 'text-ink',
  amber: 'text-data-amber',
};

/** How long a jumped-to category stays brand-tinted. */
const FLASH_MS = 1500;
/** Fixed-navbar clearance for the jump scroll. */
const JUMP_OFFSET = 84;

function MarkCell({ mark, flashed }: { mark: Mark; flashed: boolean }) {
  return (
    <div
      className="flex flex-col justify-center gap-1 px-4 py-4 transition-[background-color] duration-[800ms] md:px-[22px]"
      style={{
        backgroundColor: flashed
          ? 'color-mix(in srgb,var(--color-brand-600) 5%,var(--color-surface))'
          : 'var(--color-surface)',
      }}
    >
      <span className={`font-jetbrains text-[12.5px] font-semibold ${TONE[mark.tone]}`}>
        {mark.m}
      </span>
      <span className="font-jetbrains text-[10.5px] leading-[1.5] text-dim">{mark.note}</span>
    </div>
  );
}

/**
 * §03 — the table, the dossier's centerpiece. Six category groups with jump
 * chips (smooth scroll + 1.5s brand flash — instant, flash-free under
 * prefers-reduced-motion), 14 rows each carrying a mandatory one-line "why",
 * the verified chip and the correction invite at the head (honesty rail:
 * the timestamp lives at the point of the claim, not just the footer).
 */
export default function DossierTable({ data }: { data: CompareDossierData }) {
  const rootRef = useRef<HTMLElement>(null);
  const [flash, setFlash] = useState(-1);
  const flashTimer = useRef<number | undefined>(undefined);

  const jump = (i: number) => {
    const el = document.getElementById(`cat-${i}`);
    if (!el) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // 'instant', not 'auto': the site sets `html { scroll-behavior: smooth }`,
    // and 'auto' defers to that CSS — reduced-motion must force an instant jump.
    window.scrollTo({
      top: el.getBoundingClientRect().top + window.scrollY - JUMP_OFFSET,
      behavior: reduce ? 'instant' : 'smooth',
    });
    if (reduce) return;
    setFlash(i);
    window.clearTimeout(flashTimer.current);
    flashTimer.current = window.setTimeout(() => setFlash(-1), FLASH_MS);
  };

  useEffect(() => {
    const onJump = (e: Event) => jump((e as CustomEvent<number>).detail);
    window.addEventListener(JUMP_EVENT, onJump);
    return () => {
      window.removeEventListener(JUMP_EVENT, onJump);
      window.clearTimeout(flashTimer.current);
    };
  }, []);

  // Chip row rises with the head; each category's rows slide in as THAT group
  // enters the viewport (the table is taller than most viewports).
  useChoreo(rootRef, (root) => {
    const chips = root.querySelectorAll('[data-jump-chip]');
    if (!chips.length) return;
    return gsap.from(chips, {
      y: 10,
      opacity: 0,
      duration: 0.5,
      ease: EASE_OUT,
      stagger: 0.05,
      paused: true,
    });
  });

  useGSAP(
    () => {
      const mm = gsap.matchMedia();
      mm.add(NOT_REDUCED, () => {
        const disposers: (() => void)[] = [];
        rootRef.current?.querySelectorAll<HTMLElement>('[data-cat-group]').forEach((group) => {
          const rows = group.querySelectorAll('[data-row]');
          if (!rows.length) return;
          const tween = gsap.from(rows, {
            y: 14,
            opacity: 0,
            duration: 0.55,
            ease: EASE_OUT,
            stagger: 0.06,
            paused: true,
          });
          const dispose = playOnEnter(tween, group);
          if (dispose) disposers.push(dispose);
        });
        return () => disposers.forEach((d) => d());
      });
    },
    { scope: rootRef },
  );

  return (
    <section ref={rootRef} id="table-top" className="pt-16 md:pt-24">
      <div className={CONTAINER}>
        <div className="flex flex-wrap items-end gap-5">
          <div className="max-w-[640px]">
            <DossierHead
              num="03"
              label="FEATURE BY FEATURE"
              title={data.table.title}
              lead={data.table.lead}
              leadClassName=""
            />
          </div>
          <div className="flex flex-col gap-2.5 md:ml-auto md:items-end">
            <Chip dot>
              VERIFIED {data.verified} &middot; [1]&ndash;[{data.sources.length}]
            </Chip>
            <a
              href={routePath('contact')}
              className="text-[13px] font-semibold text-brand-600 hover:text-brand-700"
            >
              Found something out of date? Tell us &rarr;
            </a>
          </div>
        </div>

        <div className="mt-8 flex flex-wrap gap-2">
          {data.table.cats.map((cat, i) => (
            <button
              key={cat.label}
              type="button"
              data-jump-chip
              onClick={() => jump(i)}
              className="cursor-pointer rounded-full border border-line-3 bg-surface px-3.5 py-2 font-jetbrains text-[10.5px] tracking-[0.08em] text-muted transition-colors duration-200 hover:border-brand-600 hover:text-brand-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600"
            >
              {cat.label}
            </button>
          ))}
        </div>

        <div className="mt-5 overflow-x-auto">
          <div className="flex min-w-[840px] flex-col gap-px overflow-hidden rounded-ref-lg border border-line-2 bg-line-2">
            <div className={ROW_GRID}>
              <span className="bg-surface-2 px-[22px] py-3.5 font-jetbrains text-[10.5px] tracking-[0.12em] text-dim">
                FEATURE — AND WHY IT MATTERS
              </span>
              <span className="bg-surface-2 px-[22px] py-3.5 font-onest text-[14px] font-extrabold tracking-[-0.01em] text-ink">
                rii<span className="text-brand-600">.link</span>
              </span>
              <span className="bg-surface-2 px-[22px] py-3.5 font-onest text-[14px] font-extrabold tracking-[-0.01em] text-ink">
                {data.competitor}{' '}
                <span className="font-jetbrains text-[9.5px] font-normal tracking-[0.06em] text-dim">
                  {data.table.columnNote}
                </span>
              </span>
            </div>
            {data.table.cats.map((cat, i) => (
              <div key={cat.label} id={`cat-${i}`} data-cat-group className="flex flex-col gap-px">
                <span
                  className="px-[22px] py-[11px] font-jetbrains text-[10px] tracking-[0.14em] text-brand-700 transition-[background-color] duration-[800ms]"
                  style={{
                    backgroundColor:
                      flash === i
                        ? 'color-mix(in srgb,var(--color-brand-600) 14%,var(--color-surface-2))'
                        : 'var(--color-surface-2)',
                  }}
                >
                  {cat.label} &mdash; {cat.rows.length}
                </span>
                {cat.rows.map((row) => (
                  <div key={row.f} data-row className={`${ROW_GRID} bg-line`}>
                    <div
                      className="px-4 py-4 transition-[background-color] duration-[800ms] md:px-[22px]"
                      style={{
                        backgroundColor:
                          flash === i
                            ? 'color-mix(in srgb,var(--color-brand-600) 5%,var(--color-surface))'
                            : 'var(--color-surface)',
                      }}
                    >
                      <span className="block text-[14px] font-semibold text-ink">{row.f}</span>
                      <span className="mt-1 block max-w-[420px] text-[12.5px] leading-[1.55] text-muted">
                        {row.why}
                      </span>
                    </div>
                    <MarkCell mark={row.a} flashed={flash === i} />
                    <MarkCell mark={row.b} flashed={flash === i} />
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
        <MonoCaption className="mt-3.5">{data.table.footnote}</MonoCaption>
      </div>
    </section>
  );
}
